import React from "react";
import { Avatar, Box, Skeleton, Stack, Tooltip, Typography } from "@mui/material";
import { useCurrentUser } from "../../../provider/CurrentUserProvider";
import { usePresence } from "../../../provider/PresenceProvider";
import { isInternalUser } from "../../../services/backofficeUserService";
import { PRESENCE_MAX_VISIBLE_AVATARS } from "../../../services/presence";
import navigationBarPresenceStyle from "./navigationBarPresenceStyle";

const getInitials = (displayName: string) => {
  const parts = displayName
    .trim()
    .split(/\s+/)
    .filter(Boolean);

  if (parts.length === 0) {
    return "?";
  }

  return parts
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase() ?? "")
    .join("");
};

const getAvatarColor = (displayName: string) => {
  const colors = [
    "#2E7D6E",
    "#415A77",
    "#8C5E58",
    "#6C5B7B",
    "#7A6F43",
    "#3F6E8A",
    "#A15C38",
    "#5B7C5C",
  ];
  const seed = displayName.split("").reduce((accumulator, char) => accumulator + char.charCodeAt(0), 0);

  return colors[seed % colors.length];
};

export const PresenceAvatar = ({
  displayName,
  isCurrentUser,
}: {
  displayName: string;
  isCurrentUser: boolean;
}) => (
  <Avatar sx={navigationBarPresenceStyle.avatar(getAvatarColor(displayName), isCurrentUser)}>
    {getInitials(displayName)}
  </Avatar>
);

export default function NavigationBarPresence() {
  const presenceContext = usePresence();
  const currentUser = useCurrentUser();

  if (!isInternalUser(currentUser)) {
    return null;
  }

  const { presenceUsers, isPresenceLoading, isPresenceLive, presenceError } = presenceContext;

  if (presenceError !== null && presenceUsers.length === 0) {
    return (
      <Typography
        variant="caption"
        sx={[{
          color: "warning.main"
        }, navigationBarPresenceStyle.errorText]}>Presence nicht verfugbar
              </Typography>
    );
  }

  if (isPresenceLoading && presenceUsers.length === 0) {
    return (
      <Stack
        direction="row"
        spacing={1}
        sx={[{
          alignItems: "center"
        }, navigationBarPresenceStyle.loadingRoot]}>
        <Stack direction="row" sx={navigationBarPresenceStyle.loadingStack}>
          {[0, 1, 2].map((index) => (
            <Skeleton
              key={index}
              variant="circular"
              width={navigationBarPresenceStyle.avatarSize}
              height={navigationBarPresenceStyle.avatarSize}
              sx={navigationBarPresenceStyle.loadingSkeleton(index)}
            />
          ))}
        </Stack>
        <Skeleton variant="text" width={48} />
      </Stack>
    );
  }

  if (presenceUsers.length === 0) {
    return null;
  }

  const sortedUsers = [...presenceUsers].sort((leftUser, rightUser) => {
    if (leftUser.id === currentUser.id) {
      return -1;
    }

    if (rightUser.id === currentUser.id) {
      return 1;
    }

    return 0;
  });

  const visibleUsers = sortedUsers.slice(0, PRESENCE_MAX_VISIBLE_AVATARS);
  const hiddenUsersCount = Math.max(sortedUsers.length - PRESENCE_MAX_VISIBLE_AVATARS, 0);

  return (
    <Stack
      direction="row"
      spacing={1}
      sx={[{
        alignItems: "center"
      }, navigationBarPresenceStyle.root]}>
      <Stack
        direction="row"
        sx={[{
          alignItems: "center"
        }, navigationBarPresenceStyle.avatarGroup]}>
        {visibleUsers.map((user, index) => {
          const isCurrentUser = user.id === currentUser.id;
          const tooltipTitle = isCurrentUser ? `${user.displayName} (you)` : user.displayName;

          return (
            <Tooltip key={user.id} title={tooltipTitle} arrow>
              <Box
                tabIndex={0}
                aria-label={tooltipTitle}
                sx={navigationBarPresenceStyle.avatarTrigger(index)}
              >
                <Box className="presence-avatar">
                  <PresenceAvatar displayName={user.displayName} isCurrentUser={isCurrentUser} />
                </Box>
              </Box>
            </Tooltip>
          );
        })}

        {hiddenUsersCount > 0 && (
          <Tooltip title={`${hiddenUsersCount} more viewer${hiddenUsersCount > 1 ? "s" : ""}`} arrow>
            <Avatar
              aria-label={`${hiddenUsersCount} more viewers`}
              sx={navigationBarPresenceStyle.overflowAvatar}
            >
              +{hiddenUsersCount}
            </Avatar>
          </Tooltip>
        )}
      </Stack>
      <Stack direction="row" spacing={0.75} sx={{
        alignItems: "center"
      }}>
        {isPresenceLive && <Box sx={navigationBarPresenceStyle.liveDot} />}
        <Typography
          variant="caption"
          sx={[{
            color: "text.secondary"
          }, navigationBarPresenceStyle.viewingText]}>
          {presenceUsers.length} Betrachter
        </Typography>
      </Stack>
    </Stack>
  );
}
